"use client";

import { ProFeatureLockOverlay } from "@/components/ProFeatureLockOverlay";
import { TemplateFormPlaceholder } from "@/components/TemplateFormPlaceholder";
import { TemplateCreationFlow } from "@/components/templates/TemplateCreationFlow";
import { TemplateLibrary } from "@/components/templates/TemplateLibrary";
import {
  TEMPLATE_MAX_COUNT,
  type SavedTemplate,
} from "@/components/templates/templateTypes";
import "@/components/templates/templates.css";
import { ANALYTICS_EVENTS, track } from "@/lib/analyticsEvents";
import { api } from "convex/_generated/api";
import type { Id } from "convex/_generated/dataModel";
import { useQuery } from "convex/react";
import Link from "next/link";
import {
  ArrowRight,
  Check,
  FileStack,
  LayoutTemplate,
  LockKeyhole,
  Plus,
  ShieldCheck,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";

export default function TemplatesPage() {
  const me = useQuery(api.users.getMe);
  const paid = useQuery(api.userTemplates.isPaidUser);
  const templatesQuery = useQuery(api.userTemplates.listMine);
  const [creationOpen, setCreationOpen] = useState(false);
  const [replaceTemplate, setReplaceTemplate] = useState<SavedTemplate | null>(null);
  const [selectedId, setSelectedId] = useState<Id<"user_templates"> | null>(null);
  const [savedNotice, setSavedNotice] = useState<string | null>(null);
  const pageTrackedRef = useRef(false);

  const templates = (templatesQuery ?? []) as SavedTemplate[];
  const paidUnlocked = paid === true;

  useEffect(() => {
    if (templatesQuery == null || pageTrackedRef.current) return;
    pageTrackedRef.current = true;
    track(ANALYTICS_EVENTS.templatesPageViewed, {
      template_count: templatesQuery.length,
      plan_tier: paidUnlocked ? "templates_access" : "locked",
    });
  }, [paidUnlocked, templatesQuery]);

  const openCreate = () => {
    setReplaceTemplate(null);
    setSavedNotice(null);
    setCreationOpen(true);
  };

  if (me === undefined || paid === undefined || templatesQuery === undefined) {
    return <TemplatesSkeleton />;
  }

  if (me === null) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center" role="status">
        <p className="text-sm font-semibold text-muted-foreground">Preparing your template library…</p>
      </div>
    );
  }

  return (
    <main className="flex min-w-0 flex-col gap-7 pb-8">
      <header className="flex flex-col gap-5 border-b border-border pb-6 sm:flex-row sm:items-end sm:justify-between">
        <div className="min-w-0 max-w-3xl">
          <h1 className="break-words text-3xl font-bold tracking-[-0.03em] text-foreground sm:text-4xl">
            My lesson templates
          </h1>
          <p className="mt-3 max-w-2xl text-base font-semibold leading-7 text-foreground">
            Save the lesson structures you trust and let Darsi follow them in future plans.
          </p>
          <p className="mt-2 max-w-3xl text-sm leading-6 text-muted-foreground">
            Darsi studies stage order, timing, interaction patterns, procedure detail, and writing approach. New lesson content still comes from the textbook pages and class details you provide each time.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Link href="/dashboard" className="template-secondary-button">
            Create lesson plan <ArrowRight className="h-4 w-4" />
          </Link>
          {paidUnlocked && templates.length > 0 ? (
            <button type="button" onClick={openCreate} disabled={templates.length >= TEMPLATE_MAX_COUNT} className="template-primary-button">
              <Plus className="h-4 w-4" />Add template
            </button>
          ) : null}
        </div>
      </header>

      {!paidUnlocked ? (
        <ProFeatureLockOverlay
          title="Personal templates in your free trial or on Pro"
          description="Try personal templates during the active complimentary trial, then keep using them with Pro. Existing legacy access remains compatible."
          className="min-h-[min(520px,72vh)] w-full rounded-2xl"
          feature="templates"
        >
          <TemplateFormPlaceholder />
        </ProFeatureLockOverlay>
      ) : templates.length === 0 ? (
        <TemplatesEmptyState onAdd={openCreate} />
      ) : (
        <>
          {templates.length >= TEMPLATE_MAX_COUNT ? (
            <div className="flex items-start gap-3 rounded-xl border border-warning/40 bg-warning-muted p-4 text-sm" role="status">
              <FileStack className="mt-0.5 h-4 w-4 shrink-0 text-warning" />
              <p>You have reached the {TEMPLATE_MAX_COUNT}-template library limit. Delete an unused template before adding another.</p>
            </div>
          ) : null}
          {savedNotice ? (
            <div className="flex items-start gap-3 rounded-xl border border-success/35 bg-muted/45 p-4 text-sm" role="status" aria-live="polite">
              <Check className="mt-0.5 h-4 w-4 shrink-0 text-success" />
              <p>{savedNotice}</p>
            </div>
          ) : null}
          <TemplateLibrary
            templates={templates}
            selectedId={selectedId}
            onSelected={setSelectedId}
            onAdd={openCreate}
            onReplace={(template) => {
              setSavedNotice(null);
              setReplaceTemplate(template);
              setCreationOpen(true);
            }}
          />
        </>
      )}

      {paidUnlocked ? (
        <TemplateCreationFlow
          open={creationOpen}
          onClose={() => setCreationOpen(false)}
          templateCount={templates.length}
          replaceTemplate={replaceTemplate}
          onSaved={(id) => {
            setSelectedId(id);
            setSavedNotice(replaceTemplate ? "Template updated and ready to use." : "Template saved and ready to use.");
          }}
        />
      ) : null}
    </main>
  );
}

function TemplatesEmptyState({ onAdd }: { onAdd: () => void }) {
  return (
    <section className="grid gap-8 rounded-2xl border border-border bg-card p-5 sm:p-7 lg:grid-cols-[minmax(0,.9fr)_minmax(320px,1.1fr)] lg:p-9" aria-labelledby="empty-template-heading">
      <div className="flex min-w-0 flex-col justify-center">
        <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-muted text-success"><LayoutTemplate className="h-5 w-5" /></span>
        <h2 id="empty-template-heading" className="mt-5 min-w-0 break-words text-2xl font-bold tracking-tight">Keep the planning structure that already works for you</h2>
        <p className="mt-3 text-sm leading-6 text-muted-foreground">Upload one trusted lesson plan. Review the stages Darsi detects, choose when the structure applies, then save it for future lesson generation.</p>
        <ul className="mt-5 space-y-3 text-sm">
          <li className="flex items-start gap-3"><Check className="mt-0.5 h-4 w-4 shrink-0 text-success" /><span>Keep your preferred stage order and typical timing.</span></li>
          <li className="flex items-start gap-3"><Check className="mt-0.5 h-4 w-4 shrink-0 text-success" /><span>Review and correct the detected structure before saving.</span></li>
          <li className="flex items-start gap-3"><ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-success" /><span>Source previews stay protected by your authenticated account.</span></li>
        </ul>
        <button type="button" onClick={onAdd} className="template-primary-button mt-6 self-start"><Plus className="h-4 w-4" />Add your first template</button>
      </div>
      <div className="min-w-0 rounded-xl bg-muted/35 p-4 sm:p-5" aria-label="Example detected lesson structure">
        <div className="flex items-center justify-between gap-3"><p className="text-sm font-bold">Example structure</p><span className="text-xs font-semibold text-muted-foreground">Reading · 5 stages</span></div>
        <ol className="mt-4 space-y-2">
          {["Warm-up", "Pre-reading", "While-reading", "Post-reading", "Wrap-up"].map((stage, index) => (
            <li key={stage} className="grid grid-cols-[2rem_minmax(0,1fr)_4rem] items-center gap-3 rounded-lg border border-border bg-card p-3">
              <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-muted text-sm font-bold">{index + 1}</span>
              <span className="min-w-0 font-semibold">{stage}</span>
              <span className="text-right text-xs text-muted-foreground">{[5, 8, 18, 15, 4][index]} min</span>
            </li>
          ))}
        </ol>
        <div className="mt-4 flex items-start gap-3 rounded-lg border border-border bg-card p-3 text-xs leading-5 text-muted-foreground">
          <LockKeyhole className="mt-0.5 h-4 w-4 shrink-0 text-success" />Darsi follows the reusable structure—not the old lesson topic or classroom content.
        </div>
      </div>
    </section>
  );
}

function TemplatesSkeleton() {
  return (
    <main className="flex flex-col gap-6 pb-8" aria-busy="true" aria-label="Loading template library">
      <div className="space-y-3 border-b border-border pb-6"><div className="h-9 w-64 animate-pulse rounded-lg bg-muted" /><div className="h-5 w-full max-w-xl animate-pulse rounded bg-muted" /><div className="h-4 w-full max-w-2xl animate-pulse rounded bg-muted" /></div>
      <div className="grid gap-4 md:grid-cols-2"><div className="h-56 animate-pulse rounded-xl bg-muted" /><div className="h-56 animate-pulse rounded-xl bg-muted" /></div>
    </main>
  );
}
